import { Platform, Alert } from 'react-native';

/**
 * Cross-platform async confirmation dialog.
 * Uses native Alert on mobile, custom dialog on web for async behavior.
 *
 * @param title - Dialog title
 * @param message - Confirmation message
 * @param confirmText - Text for confirm button (default: "Confirm")
 * @param cancelText - Text for cancel button (default: "Cancel")
 * @returns Promise<boolean> - true if confirmed, false if canceled
 *
 * @example
 * const confirmed = await showConfirmation('Delete File', 'Are you sure?');
 * if (confirmed) {
 *   // User confirmed
 * }
 */
export const showConfirmation = async (
  title: string,
  message: string,
  confirmText: string = 'Confirm',
  cancelText: string = 'Cancel',
): Promise<boolean> => {
  return new Promise<boolean>(resolve => {
    if (Platform.OS === 'web') {
      // Web: confirm() wrapped in Promise for async consistency.
      // Note: Delete confirmation was migrated to ConfirmationDialog in AttachmentActions.
      // TODO: Replace remaining web confirm() usages (e.g. download retry) with ConfirmationDialog
      const result = window.confirm(`${title}\n\n${message}`);
      resolve(result);
    } else {
      // Native: Use React Native Alert (already async)
      Alert.alert(
        title,
        message,
        [
          {
            text: cancelText,
            style: 'cancel',
            onPress: () => resolve(false),
          },
          {
            text: confirmText,
            style: 'destructive',
            onPress: () => resolve(true),
          },
        ],
        { cancelable: false }, // Prevent dismiss by tapping outside
      );
    }
  });
};

/**
 * Show a destructive confirmation (for delete operations).
 * Uses red text on native platforms to indicate danger.
 *
 * @param title - Dialog title
 * @param message - Confirmation message
 * @param itemName - Name of item being deleted (for message context)
 * @returns Promise<boolean> - true if confirmed, false if canceled
 *
 * @example
 * const confirmed = await showDestructiveConfirmation(
 *   'Delete Attachment',
 *   'This action cannot be undone.',
 *   'document.pdf'
 * );
 */
export const showDestructiveConfirmation = async (
  title: string,
  message: string,
  itemName?: string,
): Promise<boolean> => {
  const fullMessage = itemName
    ? `${message}\n\nItem: ${itemName}`
    : message;

  return showConfirmation(title, fullMessage, 'Delete', 'Cancel');
};
